import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    IconButton,
    List,
    ListItem,
    Stack,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    assignFeature,
    createFeature,
    deleteFeature,
    getFeatures,
    removeFeature,
    updateFeature,
} from "../../../../actions/fgModuleActions";
import FeatureFormDialog from "./FeatureFormDialog";

const FeatureAssignmentPanel = ({ version, inheritedFeatures = [] }) => {
    const dispatch = useDispatch();
    const { features: allFeatures, loading } = useSelector((state) => state.fgModules);
    const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setEditDialogOpen] = useState(false);
    const [editingFeature, setEditingFeature] = useState(null);

    useEffect(() => {
        if (allFeatures.length === 0) {
            dispatch(getFeatures());
        }
    }, [dispatch, allFeatures.length]);

    const handleFeatureChange = (event, feature) => {
        if (event.target.checked) {
            dispatch(assignFeature(version.id, feature.id));
        } else {
            dispatch(removeFeature(version.id, feature.id));
        }
    };

    const handleCreateSubmit = (featureData) => {
        dispatch(createFeature(featureData));
    };

    const handleEditClick = (feature) => {
        setEditingFeature(feature);
        setEditDialogOpen(true);
    };

    const handleUpdateSubmit = (featureData) => {
        dispatch(updateFeature({ featureId: editingFeature.id, ...featureData }));
    };

    const handleDeleteClick = (featureId, featureName) => {
        if (
            window.confirm(
                `Möchten Sie das Feature "${featureName}" wirklich löschen? Es wird aus allen Modulversionen entfernt.`
            )
        ) {
            dispatch(deleteFeature(featureId));
        }
    };

    if (loading && allFeatures.length === 0) {
        return <CircularProgress size={24} />;
    }

    const assignedFeatureIds = new Set(version.features.map((f) => f.id));
    const inheritedFeatureIds = new Set(inheritedFeatures.map((f) => f.id));

    return (
        <>
            <Box mt={4}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6">Feature-Zuweisung</Typography>
                    <Button onClick={() => setCreateDialogOpen(true)}>Neues Feature</Button>
                </Box>
                <FormGroup>
                    <List dense>
                        {allFeatures.map((feature) => {
                            const isInherited = inheritedFeatureIds.has(feature.id);
                            const isChecked = assignedFeatureIds.has(feature.id) || isInherited;

                            return (
                                <ListItem
                                    key={feature.id}
                                    secondaryAction={
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                edge="end"
                                                aria-label="edit"
                                                onClick={() => handleEditClick(feature)}
                                                disabled={isInherited}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleDeleteClick(feature.id, feature.name)}
                                                disabled={isInherited}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    }
                                    disablePadding
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isChecked}
                                                disabled={isInherited}
                                                onChange={(e) => handleFeatureChange(e, feature)}
                                            />
                                        }
                                        label={`${feature.name} - ${feature.description}`}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </FormGroup>
            </Box>
            <FeatureFormDialog
                open={isCreateDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSubmit={handleCreateSubmit}
            />
            <FeatureFormDialog
                open={isEditDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                onSubmit={handleUpdateSubmit}
                initialData={editingFeature}
            />
        </>
    );
};

export default FeatureAssignmentPanel;
